import { usePathname, useSearchParams } from 'next/navigation'
import { useTranslation } from 'next-i18next'
import React, { forwardRef, HTMLAttributeAnchorTarget, memo, ReactNode, useEffect, useState } from 'react'

import type { TAbsoluteLocale, TCountry } from '@src/config/localisation/locale-config.types'
import { Link } from '@src/modules/navigation'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'

import withTracking from '../../hocs/withTracking'

export interface LinkProps {
  children: ReactNode
  onClick?: (...args: any[]) => any
  href?: string | undefined
  className?: string
  disabled?: boolean
  target?: HTMLAttributeAnchorTarget
  enableEarlyFetch?: boolean
  selectorKey?: string
  srOnlyLabel?: string
  id?: string
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  onPointerEnter?: () => void
  onPointerLeave?: () => void
  onFocus?: () => void
  onBlur?: () => void
  locale?: '' | false | TCountry | TAbsoluteLocale
  'aria-label'?: string
  'aria-haspopup'?: boolean | 'menu' | 'true'
  'aria-expanded'?: boolean
  'aria-controls'?: string
  'aria-current'?: React.AriaAttributes['aria-current']
  // Explicit test id; unlike `id` it can repeat across the DOM (e.g. desktop + mobile header rows).
  'data-testid'?: string
}

const checkExternalLink = (href?: string) => {
  if (!href) return true
  try {
    // During client-side navigation, window.location might be temporarily invalid
    // In such cases, assume it's an internal link to allow Next.js to handle navigation
    if (!window?.location?.origin) {
      return false
    }
    const url = new URL(href, window.location.origin)
    return url.origin !== window.location.origin
  } catch (e) {
    // If URL parsing fails, assume external link for safety
    return true
  }
}

const LinkWithOutTracking = forwardRef<LinkProps, any>(function LinkWithOutTrackingEl(
  {
    className,
    onClick,
    href,
    children,
    target,
    disabled,
    enableEarlyFetch = false,
    selectorKey,
    srOnlyLabel,
    id = '',
    onMouseEnter,
    onMouseLeave,
    onPointerEnter,
    onPointerLeave,
    onFocus,
    onBlur,
    locale,
    'aria-label': ariaLabel,
    'aria-haspopup': ariaHasPopup,
    'aria-expanded': ariaExpanded,
    'aria-controls': ariaControls,
    'aria-current': ariaCurrent,
    'data-testid': dataTestId,
  },
  ref: React.ForwardedRef<any>,
) {
  const { t } = useTranslation('common')
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const isI18nPreviewEnabled = useI18nPreviewContext()

  const [isExternalLink, setIsExternalLink] = useState(checkExternalLink.bind(null, href))

  useEffect(() => {
    setIsExternalLink(checkExternalLink(href))
  }, [href])

  /**
   * NOTE: NextLink doesn't play well with hash updates
   *  */
  const checkHashUpdate = () => {
    if (!href) return true
    try {
      // During client-side navigation, window.location might be temporarily invalid
      if (!window?.location?.origin) {
        return false
      }
      const targetURL = new URL(href, window.location.origin)
      const targetURLWithoutHash = targetURL.origin + targetURL.pathname + (targetURL.search || '?')
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const currentURLWithoutHash = window.location.origin + pathname + '?' + searchParams?.toString()
      const isSamePath = targetURLWithoutHash === currentURLWithoutHash
      const isSameHash = window.location.hash === targetURL.hash
      return isSamePath && !isSameHash
    } catch (error) {
      // If URL construction fails during navigation, return false
      return false
    }
  }

  const childrenText = typeof children === 'string' ? children : t('link')
  const computedAriaLabel =
    ariaLabel || (target === '_blank' ? t('opens_in_new_tab', { linkText: childrenText }) : undefined)

  return isExternalLink || checkHashUpdate() ? (
    <a
      {...(id ? { id, ['data-testid']: id } : {})}
      // Explicit data-testid spreads second so it wins over the id-derived one when both are set.
      {...(dataTestId ? { ['data-testid']: dataTestId } : {})}
      ref={ref}
      href={href}
      target={target}
      aria-label={computedAriaLabel}
      aria-haspopup={ariaHasPopup}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      aria-current={ariaCurrent}
      className={className}
      onClick={onClick}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-selector-key={selectorKey}
      aria-disabled={disabled ? 'true' : undefined}
    >
      {srOnlyLabel && <span className="sr-only">{srOnlyLabel}</span>}
      {children}
    </a>
  ) : (
    <Link
      {...(id ? { id, ['data-testid']: id } : {})}
      // Explicit data-testid spreads second so it wins over the id-derived one when both are set.
      {...(dataTestId ? { ['data-testid']: dataTestId } : {})}
      ref={ref}
      href={href}
      target={target}
      aria-label={computedAriaLabel}
      aria-haspopup={ariaHasPopup}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      aria-current={ariaCurrent}
      className={className}
      onClick={onClick}
      aria-disabled={disabled ? 'true' : undefined}
      tabIndex={disabled ? -1 : undefined}
      data-selector-key={selectorKey}
      prefetch={enableEarlyFetch}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      locale={isI18nPreviewEnabled ? locale : undefined}
    >
      {srOnlyLabel && <span className="sr-only">{srOnlyLabel}</span>}
      {children}
    </Link>
  )
})

export { LinkWithOutTracking }

export default memo(withTracking(LinkWithOutTracking, true))
